import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

/**
 * Shared form primitives for the camera configuration panels.
 *
 * Every ONVIF panel used to carry its own copy of these class strings and its own
 * one-off markup for a slider or a label, which is why the tabs never quite matched
 * each other. Keeping them here means a control looks and behaves the same on the
 * Image tab as it does on Encoding, and the panels are left to describe *what* the
 * camera can do rather than how a select should be spaced.
 */

export const inputCls =
  'w-full rounded-lg border border-border bg-muted px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-ring focus:border-brand disabled:opacity-40 disabled:cursor-not-allowed transition-shadow';
export const labelCls = 'text-[11px] font-medium text-muted-foreground mb-1 block';

/** Wraps a control with its label, optional unit hint and validation message. */
export function Field({ label, hint, error, htmlFor, children, className = '' }: any) {
  return (
    <div className={className}>
      {label && (
        <label htmlFor={htmlFor} className={labelCls}>
          {label}
          {hint && <span className="ml-1 font-normal text-muted-foreground">{hint}</span>}
        </label>
      )}
      {children}
      {error && <p className="mt-1 text-[11px] font-medium text-destructive">{error}</p>}
    </div>
  );
}

/**
 * A range control whose value can also be typed. Dragging is quick but imprecise, and
 * these are camera settings a technician often wants to set to an exact number they were
 * given — so the readout doubles as an input, clamped to the camera's own range.
 */
export function Slider({ label, value, range, step, onChange, disabled, hint }: any) {
  const min = Number(range?.min ?? 0);
  const max = Number(range?.max ?? 100);
  // Camera ranges are often fractional (0–1 gains); pick a step that can actually reach them.
  const resolved = step ?? (max - min <= 2 ? 0.01 : 1);
  const [text, setText] = useState<string>(value == null ? '' : String(value));

  useEffect(() => { setText(value == null ? '' : String(value)); }, [value]);

  const commit = (raw: string) => {
    if (raw === '') { setText(value == null ? '' : String(value)); return; }
    const clamped = Math.min(max, Math.max(min, Number(raw)));
    if (Number.isNaN(clamped)) { setText(value == null ? '' : String(value)); return; }
    setText(String(clamped));
    onChange(clamped);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12px] font-medium text-foreground">
          {label}
          {hint && <span className="ml-1 font-normal text-muted-foreground">{hint}</span>}
        </span>
        <input
          type="number"
          value={text}
          min={min}
          max={max}
          step={resolved}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit((e.target as HTMLInputElement).value); }}
          aria-label={`${label} value`}
          className="w-16 rounded-md border border-border bg-muted px-2 py-1 text-right text-[12px] tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:opacity-40"
        />
      </div>
      <input
        type="range" min={min} max={max} step={resolved} value={value ?? min}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        aria-label={label}
        className="w-full h-2 rounded-full appearance-none bg-muted accent-brand cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      />
      <div className="flex justify-between text-[10px] tabular-nums text-muted-foreground">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

export function Choice({ label, value, options, labels, onChange, disabled, hint, error, placeholder = 'Select…' }: any) {
  return (
    <Field label={label} hint={hint} error={error}>
      <div className="relative">
        <select
          value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          disabled={disabled} className={`${inputCls} appearance-none pr-8`}
        >
          {value == null && <option value="">{placeholder}</option>}
          {(options || []).map((o: any, i: number) => (
            <option key={String(o)} value={String(o)}>{labels?.[i] ?? String(o)}</option>
          ))}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      </div>
    </Field>
  );
}

export function NumberField({ label, value, range, onChange, disabled, error, hint }: any) {
  const rangeHint = range ? `(${range.min}–${range.max})` : '';
  return (
    <Field label={label} hint={hint || rangeHint} error={error}>
      <input
        type="number" value={value ?? ''} min={range?.min} max={range?.max}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        disabled={disabled} className={`${inputCls} tabular-nums`}
      />
    </Field>
  );
}

export function TextField({ label, value, onChange, disabled, placeholder, error, hint, type = 'text', mono }: any) {
  return (
    <Field label={label} hint={hint} error={error}>
      <input
        type={type} value={value ?? ''} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled} className={`${inputCls} ${mono ? 'font-mono' : ''}`}
      />
    </Field>
  );
}

/**
 * A real switch rather than a checkbox — these are on/off camera features, not form consent.
 *
 * The whole row is the control, not just the 36px switch: these sit among full-width selects and
 * sliders, and a switch alone left the label inert and the target a long reach away from the text
 * that named it. Bordering the row like an input also stops a toggle reading as loose prose
 * between two proper fields.
 */
export function Toggle({ label, description, checked, onChange, disabled }: any) {
  const descriptionId = description
    ? `toggle-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-')}-description`
    : undefined;
  return (
    <button
      type="button" role="switch" aria-checked={!!checked} aria-describedby={descriptionId}
      disabled={disabled} onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-4 rounded-lg border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-border"
    >
      <span className="min-w-0">
        <span className="block text-[13px] font-medium text-foreground">{label}</span>
        {description && (
          <span id={descriptionId} className="mt-0.5 block text-[11px] text-muted-foreground">
            {description}
          </span>
        )}
      </span>
      {/* The track keeps its border in both states, only recolouring it, so the knob doesn't
          shift by a pixel as the off state gains a border the on state lacked. */}
      <span
        aria-hidden="true"
        className={`relative h-5 w-9 flex-shrink-0 rounded-full border transition-colors ${
          checked ? 'border-brand bg-brand' : 'border-border bg-muted'
        }`}
      >
        {/* Centred by inset rather than a translate, so the transform is free for the slide and
            the knob stays put vertically whatever the border does. */}
        <span
          className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white shadow-sm transition-all ${
            checked ? 'left-4' : 'left-0.5'
          }`}
        />
      </span>
    </button>
  );
}

/** Two-or-three-way segmented choice, for settings where seeing all options at once helps. */
export function Segmented({ label, value, options, onChange, disabled }: any) {
  return (
    <Field label={label}>
      <div className="flex gap-1 rounded-lg border border-border bg-muted p-1">
        {options.map((o: any) => {
          const active = value === o.value;
          return (
            <button
              key={String(o.value)} type="button" disabled={disabled}
              onClick={() => onChange(o.value)}
              className={`flex-1 rounded-md py-1.5 text-[12px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                active ? 'bg-brand text-white shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

/**
 * A collapsible group of related settings. Collapsing matters here: a WDR dome reports
 * enough controls that the Image tab runs several screens long, and most visits only touch
 * one group.
 */
export function Section({ title, hint, children, defaultOpen = true, collapsible = true, badge }: any) {
  const [open, setOpen] = useState(defaultOpen);
  if (!collapsible) {
    return (
      <div className="border-t border-border pt-4 first:border-0 first:pt-0">
        <div className="mb-3">
          <p className="text-[13px] font-semibold text-foreground">{title}</p>
          {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
        </div>
        <div className="space-y-3">{children}</div>
      </div>
    );
  }
  return (
    <div className="border-t border-border pt-3 first:border-0 first:pt-0">
      <button
        type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        className="group flex w-full items-center gap-2 rounded-lg py-1.5 text-left transition-colors hover:bg-muted"
      >
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`}
        />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-foreground">
            {title}
            {badge != null && (
              <span className="ml-2 rounded-full bg-brand-light px-2 py-0.5 text-[10px] font-medium text-brand align-middle">
                {badge}
              </span>
            )}
          </p>
          {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
        </div>
      </button>
      {open && <div className="space-y-3.5 pt-3 pb-1 pl-6">{children}</div>}
    </div>
  );
}

/** Read-only value, shown as a value rather than as a disabled input pretending to be editable. */
export function InfoRow({ label, value, mono }: any) {
  return (
    <div className="min-w-0">
      <p className={labelCls}>{label}</p>
      <p className={`truncate text-[13px] text-foreground ${mono ? 'font-mono' : ''}`}>
        {value == null || value === '' ? <span className="text-muted-foreground">—</span> : value}
      </p>
    </div>
  );
}

export function Notice({ tone = 'muted', icon: Icon, children }: any) {
  const tones: Record<string, string> = {
    muted: 'border-border bg-muted text-muted-foreground',
    warn: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200',
    info: 'border-brand-ring bg-brand-lighter text-foreground',
  };
  return (
    <div className={`flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-[12px] ${tones[tone]}`}>
      {Icon && <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />}
      <div className="min-w-0 flex-1 leading-relaxed">{children}</div>
    </div>
  );
}

/** The card every panel sits in, so the tabs share one frame instead of five near-identical ones. */
export function PanelCard({ title, description, actions, children, footer }: any) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-card">
      <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-4">
        <div className="min-w-0">
          <h3 className="text-[14px] font-semibold text-foreground">{title}</h3>
          {description && <p className="mt-0.5 text-[11px] text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
      </div>
      <div className="space-y-1 px-5 pb-5">{children}</div>
      {footer}
    </div>
  );
}

/**
 * Sticky save bar. The Image tab is long enough that a save button at the bottom is a scroll
 * away from the control you just changed, and "did that apply?" is exactly the question this
 * page should never leave open — so the bar follows you, and says what is pending.
 */
export function PanelFooter({ dirty, dirtyLabel, loading, onReset, onSave, saveLabel = 'Apply changes', disabled, note }: any) {
  return (
    <div className="sticky bottom-0 z-10 rounded-b-xl border-t border-border bg-card px-5 py-3 backdrop-blur supports-[backdrop-filter]:bg-card">
      <div className="flex items-center justify-between gap-3">
        <p className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
          {loading ? 'Applying to camera…' : dirty ? (dirtyLabel || 'Unsaved changes') : note || 'No pending changes'}
        </p>
        <div className="flex flex-shrink-0 items-center gap-2">
          {onReset && (
            <button
              type="button" onClick={onReset} disabled={loading || !dirty}
              className="rounded-lg border border-border px-3 py-2 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Reset
            </button>
          )}
          <button
            type="button" onClick={onSave} disabled={loading || disabled || !dirty}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-[12px] font-medium text-white shadow-sm transition-colors hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
            {loading ? 'Applying…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function EmptyState({ children }: any) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-card px-6 py-10 text-center text-[13px] text-muted-foreground">
      {children}
    </div>
  );
}

/** Reports dirty state to the page (for the tab indicator and the leave guard). */
export function useReportDirty(dirty: boolean, onDirtyChange?: (d: boolean) => void) {
  const last = useRef<boolean | null>(null);
  useEffect(() => {
    if (last.current === dirty) return;
    last.current = dirty;
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  // Report "clean" when the panel unmounts, so a tab the user left doesn't stay flagged.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
}
